import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLang } from '../lib/lang';
import { getLegalPagePublic } from '../lib/api';
import Footer from '../components/Footer';
import { Loader2, FileText, Shield, Cookie, Heart } from 'lucide-react';

const ICONS = {
  terms: FileText,
  privacy: Shield,
  cookies: Cookie,
  'responsible-play': Heart,
};

export default function LegalPage({ defaultSlug = null }) {
  const params = useParams();
  const slug = defaultSlug || params.slug;
  const { lang } = useLang();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setNotFound(false);
    getLegalPagePublic(slug)
      .then((res) => setPage(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const title = page ? (lang === 'fr' ? page.title_fr : page.title_en) : '';
  const content = page ? (lang === 'fr' ? page.content_fr : page.content_en) : '';
  const Icon = ICONS[slug] || FileText;

  return (
    <div className="min-h-screen bg-rolex-green flex flex-col">
      {/* Hero */}
      <div className="relative py-14 sm:py-20 px-4 sm:px-6 lg:px-8 overflow-hidden border-b border-[hsl(var(--gold)/0.18)]">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(212,175,55,0.10),transparent_70%)] pointer-events-none" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gold-gradient mb-5 shadow-2xl">
            <Icon className="w-8 h-8 text-rolex-green" />
          </div>
          {loading ? (
            <div className="h-10 w-64 mx-auto bg-cream/10 rounded animate-pulse" />
          ) : (
            <h1 className="font-display text-4xl sm:text-5xl font-black text-cream tracking-tight">{title}</h1>
          )}
        </div>
      </div>

      {/* Content */}
      <main className="flex-1 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto bg-cream rounded-2xl shadow-2xl border-2 border-gold/30 px-6 py-10 sm:px-12 sm:py-14">
          {loading && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-10 h-10 animate-spin text-gold" />
            </div>
          )}
          {notFound && !loading && (
            <div className="text-center py-20 text-rolex-green/65">
              <p className="text-lg font-semibold mb-2">
                {lang === 'fr' ? 'Page introuvable ou désactivée' : 'Page not found or disabled'}
              </p>
              <p className="text-sm">
                {lang === 'fr' ? 'Cette page n\u2019est pas disponible actuellement.' : 'This page is currently unavailable.'}
              </p>
            </div>
          )}
          {!loading && !notFound && page && (
            <article className="prose prose-stone max-w-none
              prose-headings:font-display prose-headings:text-rolex-green prose-headings:tracking-tight
              prose-h1:text-3xl prose-h1:font-black prose-h1:mb-5
              prose-h2:text-2xl prose-h2:font-bold prose-h2:mt-8 prose-h2:mb-3 prose-h2:text-rolex-green
              prose-h3:text-lg prose-h3:font-bold prose-h3:mt-5 prose-h3:mb-2 prose-h3:text-gold-dark
              prose-p:text-rolex-green/85 prose-p:leading-relaxed prose-p:my-3
              prose-strong:text-rolex-green prose-strong:font-semibold
              prose-a:text-gold-dark prose-a:no-underline hover:prose-a:underline
              prose-ul:my-3 prose-li:text-rolex-green/85 prose-li:my-1
              prose-em:text-rolex-green/65 prose-em:text-sm">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{content || ''}</ReactMarkdown>
            </article>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
